import { cn } from "@newxsystems/shared";
import { Icon } from "@templates/design-system";
import { DaylightCardLink } from "../../../../modules/content/components/DaylightCardLink/DaylightCardLink";
import { DaylightCommonContent } from "../../../../modules/content/components/DaylightCommonContent/DaylightCommonContent";
import { DaylightRichText } from "../../../../modules/content/components/DaylightRichText/DaylightRichText";
import { DaylightSection } from "../../../../modules/content/components/DaylightSection/DaylightSection";
import type { IIconContentGridBlockProps } from "./IconContentGridBlock.props";

const columns = {
  1: "lg:grid-cols-1",
  2: "lg:grid-cols-2",
  3: "lg:grid-cols-3",
  4: "lg:grid-cols-4",
} as const;
const spans = {
  1: "lg:col-span-1",
  2: "lg:col-span-2",
  3: "lg:col-span-3",
  4: "lg:col-span-4",
} as const;

export const IconContentGridBlock = ({
  alignment,
  anchorId,
  buttons,
  columnCount,
  description,
  eyebrow,
  heading,
  items,
  parentClass,
  spacing,
}: IIconContentGridBlockProps) => {
  if (!items?.length) return null;

  return (
    <DaylightSection className={parentClass} id={anchorId} spacing={spacing}>
      <div className="flex flex-col gap-12 md:gap-16">
        <DaylightCommonContent
          alignment={alignment}
          buttons={buttons}
          description={description}
          eyebrow={eyebrow}
          heading={heading}
          layout="rail"
        />
        <div
          className={cn(
            "grid grid-cols-1 border-l border-t border-border sm:grid-cols-2",
            columns[columnCount || 3],
          )}
        >
          {items.map((item, index) => (
            <DaylightCardLink
              className={cn(
                "relative border-b border-r border-border p-8 transition-colors duration-300 hover:bg-panel",
                spans[item.columnSpan || 1],
              )}
              key={index}
              link={item.cardLink}
            >
              <div className="mb-8 flex items-start justify-between">
                <span className="daylight-label text-northsea">
                  {String(index + 1).padStart(2, "0")}
                </span>
                {item.icon ? (
                  <Icon
                    className="size-6 text-northsea transition-colors duration-300 group-hover:text-primary"
                    name={item.icon}
                  />
                ) : null}
              </div>
              <h3 className="mb-4 font-daylight-display text-2xl font-semibold tracking-[-0.04em] text-graphite">
                {item.heading}
              </h3>
              {item.description ? (
                <DaylightRichText
                  className="mb-8 min-h-20 text-sm leading-relaxed text-northsea"
                  content={item.description}
                />
              ) : null}
              {item.facts?.length ? (
                <ul className="max-h-0 space-y-2 overflow-hidden opacity-0 transition-all duration-300 group-focus-visible:max-h-40 group-focus-visible:border-t group-focus-visible:border-border group-focus-visible:pt-4 group-focus-visible:opacity-100 group-hover:max-h-40 group-hover:border-t group-hover:border-border group-hover:pt-4 group-hover:opacity-100 motion-reduce:max-h-none motion-reduce:border-t motion-reduce:border-border motion-reduce:pt-4 motion-reduce:opacity-100 motion-reduce:transition-none">
                  {item.facts.map((fact, factIndex) => (
                    <li
                      className="daylight-label-micro flex items-center justify-between gap-4"
                      key={factIndex}
                    >
                      <span className="text-northsea">{fact.label}</span>
                      <span className="text-primary">{fact.value}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
              {item.cardLink ? (
                <Icon
                  className="absolute bottom-6 right-6 size-5 translate-x-2 translate-y-2 text-primary opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:translate-y-0 group-hover:opacity-100"
                  name="arrow-up-right"
                />
              ) : null}
            </DaylightCardLink>
          ))}
        </div>
      </div>
    </DaylightSection>
  );
};

IconContentGridBlock.displayName = "Block::IconContentGridBlock";
